/**
 * renderSessionSurface — orchestrate one "source dashboard" section.
 *
 * Each source dashboard (Rapsodo, Pocket Radar, future MVP-2 assessment)
 * has the same shape: a sortable sessions table, an across-sessions
 * trend chart, and a per-session detail panel with a session picker that
 * filters one or more detail-chart components. This function centralizes
 * the wiring between those pieces.
 *
 * The seam takes a per-source **surface descriptor** — see
 * src/features/<source>/surface.js — that names the relevant DOM IDs,
 * declares which detail charts to mount, supplies the right aggregator,
 * delete query, and the source-specific copy strings. The coordinator's
 * invariants (compare-button enable/disable handshake, "force picker
 * into single mode + scroll into view" sequence, active-row sync,
 * re-render-after-delete) live here, not in the descriptors.
 *
 * Invariants:
 *   - When the picker is in single or compare2 mode, the date-range
 *     "Compare to previous period" button is force-disabled. When
 *     returning to All mode, it's re-enabled.
 *   - The sessions table's activeSessionId tracks the picker's single
 *     selection (cleared in any other mode).
 *   - Successful deletes call onReRender to rebuild the surface.
 */

export function renderSessionSurface(root, { descriptor, rows, sessions, onReRender }) {
  const sectionEl = root.querySelector(descriptor.sectionId);
  if (!sectionEl) return;
  sectionEl.hidden = false;

  const aggregated = descriptor.aggregate(sessions, rows);
  const labels = Object.fromEntries(sessions.map((s) => [s.id, s.label]));

  // Sessions table.
  const tableEl = root.querySelector(descriptor.tableId);
  tableEl.rows = aggregated;

  // Across-sessions categorical chart.
  const chartWrap = root.querySelector(descriptor.chartWrapId);
  chartWrap.innerHTML = `<ta-sessions-chart kind="${descriptor.chartKind}"></ta-sessions-chart>`;
  const chart = chartWrap.querySelector('ta-sessions-chart');
  chart.rows = aggregated;

  // Detail panel + detail-chart components.
  const detailEl = root.querySelector(descriptor.detailId);
  const detailSubEl = root.querySelector(descriptor.detailSubId);
  const detailCharts = descriptor.detailCharts.map(({ containerId, tag, rowsProp }) => {
    const containerEl = root.querySelector(containerId);
    containerEl.innerHTML = `<${tag}></${tag}>`;
    const chartEl = containerEl.querySelector(tag);
    chartEl[rowsProp] = rows;
    chartEl.sessionLabels = labels;
    return chartEl;
  });

  // Picker.
  const pickerEl = root.querySelector(descriptor.pickerId);
  pickerEl.sessions = sessions;

  // Compare button (optional — Rapsodo has one, PR doesn't).
  const compareBtn = descriptor.compareButtonId
    ? root.querySelector(descriptor.compareButtonId)
    : null;

  function pushFilter(filter) {
    for (const chartEl of detailCharts) chartEl.sessionFilter = filter;
    tableEl.activeSessionId = filter.mode === 'single' ? filter.sessionA : null;
  }

  function disableCompareButton() {
    if (!compareBtn) return;
    setCompareBtnActive(compareBtn, false);
    for (const chartEl of detailCharts) chartEl.compareEnabled = false;
    compareBtn.setAttribute('disabled', 'disabled');
    compareBtn.setAttribute('aria-disabled', 'true');
    compareBtn.setAttribute('title', 'Not available in single/compare-2 session mode');
  }

  function enableCompareButton() {
    if (!compareBtn) return;
    compareBtn.removeAttribute('disabled');
    compareBtn.removeAttribute('aria-disabled');
    compareBtn.removeAttribute('title');
  }

  function showDetail(sessionId) {
    detailEl.hidden = false;
    const session = aggregated.find((s) => s.id === sessionId);
    detailSubEl.textContent = session ? descriptor.detailLabel(session) : descriptor.detailFallbackLabel;
    pickerEl.setSingleSession(sessionId);
    pushFilter(pickerEl.currentFilter());
    disableCompareButton();
    detailEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // Row clicks on the table or the across-sessions chart drill into the detail panel.
  tableEl.addEventListener('session-row-click', (e) => showDetail(e.detail.sessionId));
  chart.addEventListener('session-row-click', (e) => showDetail(e.detail.sessionId));

  // Per-row × → confirm + delete + re-render.
  tableEl.addEventListener('session-delete-request', async (e) => {
    const sessionId = e.detail.sessionId;
    const session = aggregated.find((s) => s.id === sessionId);
    const label = session?.label ?? 'this session';
    const ok = window.confirm(descriptor.deleteConfirmCopy(label));
    if (!ok) return;
    try {
      await descriptor.deleteSession(sessionId);
      onReRender?.();
    } catch (err) {
      window.alert(`Couldn't delete: ${err.message ?? err}`);
    }
  });

  // Picker filter changes (user-driven).
  pickerEl.addEventListener('session-filter-change', (e) => {
    const filter = e.detail;
    pushFilter(filter);
    if (filter.mode !== 'all') {
      disableCompareButton();
    } else {
      enableCompareButton();
    }
  });

  // Compare-button click (when present).
  if (compareBtn) {
    compareBtn.addEventListener('click', () => {
      if (compareBtn.disabled) return;
      const next = compareBtn.dataset.active !== 'true';
      setCompareBtnActive(compareBtn, next);
      for (const chartEl of detailCharts) chartEl.compareEnabled = next;
    });
  }
}

// ============================================================
// Helpers
// ============================================================

function setCompareBtnActive(btn, active) {
  if (!btn) return;
  btn.dataset.active = String(active);
  const icon = btn.querySelector('.compare-toggle__icon');
  const label = btn.querySelector('.compare-toggle__label');
  if (icon) icon.textContent = active ? '×' : '+';
  if (label) label.textContent = active ? 'Comparing to previous period' : 'Compare to previous period';
}
